import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileText, Download, Calendar, Filter, BarChart3, TrendingUp, Users, Clock } from 'lucide-react';
import { toast } from 'react-toastify';
import { getTatAnalysisReport } from '../actions/tatAnalysisAction';
import { getMyVerificationProcesses } from '../actions/verificationProcessActions';
import { GET_TAT_ANALYSIS_REPORT_RESET } from '../constants/tatAnalysisConstant';

const TatAnalysisReport = () => {
    const dispatch = useDispatch();

    // Redux state selectors
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const tatAnalysisReportInfo = useSelector((state) => state.tatAnalysisReportInfo);
    const { loadingTatAnalysisReport, errorTatAnalysisReport, tatAnalysisReportData } = tatAnalysisReportInfo;

    const getVerificationProcessesInfo = useSelector((state) => state.getVerificationProcessesInfo);
    const { getVerificationProcessesData } = getVerificationProcessesInfo;

    // Local state
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        processId: ''
    });

    const tatCategories = ['0-2', '2-4', '4-6', '6-8', '>8'];
    const assignedProcesses = getVerificationProcessesData?.data || [];
    const reportData = tatAnalysisReportData?.data || null;

    // Get verification processes on component mount
    useEffect(() => {
        if (userInfo) {
            dispatch(getMyVerificationProcesses());
            // Load initial report
            dispatch(getTatAnalysisReport());
        }
    }, [dispatch, userInfo]);

    // Handle errors
    useEffect(() => {
        if (errorTatAnalysisReport) {
            toast.error(errorTatAnalysisReport);
        }
    }, [errorTatAnalysisReport]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleGenerateReport = () => {
        const params = new URLSearchParams();
        
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.processId) params.append('processId', filters.processId);

        dispatch(getTatAnalysisReport(params.toString()));
    };

    const handleClearFilters = () => {
        setFilters({
            startDate: '',
            endDate: '',
            processId: ''
        });
        dispatch(getTatAnalysisReport());
    };

    const handleExportReport = () => {
        if (!reportData) {
            toast.error('No data to export');
            return;
        }

        // Create CSV content
        let csvContent = "TAT Analysis Report\n\n";
        
        // Summary
        csvContent += "Summary\n";
        csvContent += `Total Cases,${reportData.summary.totalCases}\n`;
        csvContent += `Overall Weighted Average,${reportData.summary.overallWeightedAvg}%\n`;
        csvContent += `Total Entries,${reportData.summary.totalEntries}\n`;
        csvContent += `Date Range,${reportData.summary.dateRange.startDate} to ${reportData.summary.dateRange.endDate}\n\n`;

        // Category breakdown
        csvContent += "Category Breakdown\n";
        csvContent += "Category,Total Cases,Weighted Average %\n";
        tatCategories.forEach(category => {
            const categoryData = reportData.categoryStats[category];
            csvContent += `${category} Hours,${categoryData.totalCases},${categoryData.avgTatValue.toFixed(2)}\n`;
        });

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tat-analysis-report-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">TAT Analysis Report</h1>
                        <p className="text-gray-600 mt-1">Comprehensive turnaround time analysis with weighted averages</p>
                    </div>
                    <button
                        onClick={handleExportReport}
                        disabled={!reportData}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Download size={18} />
                        Export Report
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter size={18} className="text-gray-500" />
                        <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                            <input
                                type="date"
                                name="startDate"
                                value={filters.startDate}
                                onChange={handleFilterChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                            <input
                                type="date"
                                name="endDate"
                                value={filters.endDate}
                                onChange={handleFilterChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Verification Process</label>
                            <select
                                name="processId"
                                value={filters.processId}
                                onChange={handleFilterChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
                            >
                                <option value="">All Processes</option>
                                {assignedProcesses.map(process => (
                                    <option key={process._id} value={process._id}>
                                        {process.process}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-end gap-2">
                            <button
                                onClick={handleGenerateReport}
                                disabled={loadingTatAnalysisReport}
                                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {loadingTatAnalysisReport ? 'Generating...' : 'Generate'}
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loadingTatAnalysisReport && (
                    <div className="bg-white rounded-lg border border-gray-200 p-12">
                        <div className="text-center text-gray-500">
                            <BarChart3 size={48} className="mx-auto mb-4 text-gray-300 animate-pulse" />
                            <div className="text-lg font-medium">Generating Report...</div>
                            <div className="text-sm">Please wait while we analyze your data</div>
                        </div>
                    </div>
                )}

                {/* Report Content */}
                {!loadingTatAnalysisReport && reportData && (
                    <>
                        {/* Statistics Overview */}
                        <div className="bg-white rounded-lg border border-gray-200">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-lg font-medium text-gray-900">Overview</h2>
                            </div>
                            <div className="p-6">
                                <div className="space-y-6">
                                    {/* Key Metrics */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                                            <div className="flex items-center justify-center mb-2">
                                                <Users size={24} className="text-blue-600" />
                                            </div>
                                            <div className="text-2xl font-semibold text-gray-900">
                                                {reportData.summary.totalCases.toLocaleString()}
                                            </div>
                                            <div className="text-sm text-gray-600">Total Cases</div>
                                        </div>
                                        <div className="text-center p-4 bg-green-50 rounded-lg">
                                            <div className="flex items-center justify-center mb-2">
                                                <TrendingUp size={24} className="text-green-600" />
                                            </div>
                                            <div className="text-2xl font-semibold text-gray-900">
                                                {reportData.summary.overallWeightedAvg.toFixed(1)}%
                                            </div>
                                            <div className="text-sm text-gray-600">Weighted Average TAT</div>
                                        </div>
                                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                                            <div className="flex items-center justify-center mb-2">
                                                <FileText size={24} className="text-purple-600" />
                                            </div>
                                            <div className="text-2xl font-semibold text-gray-900">
                                                {reportData.summary.totalEntries}
                                            </div>
                                            <div className="text-sm text-gray-600">Total Entries</div>
                                        </div>
                                    </div>

                                    {/* Category Breakdown */}
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-700 mb-3">Category Breakdown</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                            {tatCategories.map((category) => {
                                                const categoryData = reportData.categoryStats[category] || { totalCases: 0, avgTatValue: 0 };
                                                return (
                                                    <div key={category} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Clock size={16} className="text-gray-500" />
                                                            <div className="text-xs text-gray-500">{category} Hours</div>
                                                        </div>
                                                        <div className="text-lg font-medium text-gray-900">
                                                            {categoryData.totalCases.toLocaleString()}
                                                        </div>
                                                        <div className="text-xs text-gray-600">
                                                            {categoryData.avgTatValue.toFixed(1)}% weighted avg
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Process Breakdown */}
                        {reportData.processBreakdown && reportData.processBreakdown.length > 0 && (
                            <div className="bg-white rounded-lg border border-gray-200">
                                <div className="p-6 border-b border-gray-200">
                                    <h2 className="text-lg font-medium text-gray-900">Process Breakdown</h2>
                                </div>
                                <div className="p-6">
                                    <div className="space-y-6">
                                        {reportData.processBreakdown.map((processData, index) => (
                                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h4 className="text-lg font-medium text-gray-900">{processData.processName}</h4>
                                                    <div className="text-right">
                                                        <div className="text-lg font-semibold text-gray-900">
                                                            {processData.totalCases.toLocaleString()} cases
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            {processData.avgTatValue.toFixed(1)}% weighted avg
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                                    {tatCategories.map((category) => {
                                                        const categoryData = processData.categories[category];
                                                        return (
                                                            <div key={category} className="p-3 bg-gray-50 rounded border">
                                                                <div className="text-xs text-gray-600 mb-1">{category}h</div>
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {categoryData.totalCases.toLocaleString()}
                                                                </div>
                                                                <div className="text-xs text-gray-600">
                                                                    {categoryData.avgTatValue.toFixed(1)}%
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Date Breakdown */}
                        {reportData.dateBreakdown && reportData.dateBreakdown.length > 0 && (
                            <div className="bg-white rounded-lg border border-gray-200">
                                <div className="p-6 border-b border-gray-200">
                                    <h2 className="text-lg font-medium text-gray-900">Date-wise Analysis</h2>
                                </div>
                                <div className="p-6">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Cases</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Weighted Avg</th>
                                                    {tatCategories.map(category => (
                                                        <th key={category} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                                                            {category}h
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {reportData.dateBreakdown.slice(0, 10).map((dateData, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                            {new Date(dateData.date).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-center text-gray-900">
                                                            {dateData.totalCases.toLocaleString()}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-center text-gray-900">
                                                            {dateData.avgTatValue.toFixed(1)}%
                                                        </td>
                                                        {tatCategories.map(category => {
                                                            const categoryData = dateData.categories[category];
                                                            return (
                                                                <td key={category} className="px-2 py-3 text-xs text-center">
                                                                    {categoryData.totalCases > 0 ? (
                                                                        <div>
                                                                            <div className="font-medium">{categoryData.totalCases}</div>
                                                                            <div className="text-gray-500">{categoryData.avgTatValue.toFixed(1)}%</div>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-gray-400">-</span>
                                                                    )}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* No Data State */}
                {!loadingTatAnalysisReport && !reportData && (
                    <div className="bg-white rounded-lg border border-gray-200 p-12">
                        <div className="text-center text-gray-500">
                            <BarChart3 size={48} className="mx-auto mb-4 text-gray-300" />
                            <div className="text-lg font-medium mb-2">No Report Data</div>
                            <div className="text-sm">Click "Generate" to create your TAT analysis report</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TatAnalysisReport;